var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MulticastDelegate1, MulticastDelegate2, generateUUID } from "flavr-common";
import { preferencesService } from "../../preferences/PreferencesService";
import { loggingService } from "../server/LoggingService";
import { fetchWebRTCStunTurnServers, setVideoTrackContentHint } from "../mediaStream/IceServers";
import { usersService } from "../UsersService";
import { Device } from "../mediaStream/Device";
import { getSupportedAudioCodecs } from "../mediaStream/Utils";
const PC_PROPRIETARY_CONSTRAINTS = {
    optional: [{ googDscp: true }]
};
const DISCONNECT_TIMEOUT = 5 * 60 * 1000;
export class MediaConnections {
    constructor(peer) {
        this.peer = peer;
        this.onReadyChangedDelegate = new MulticastDelegate1();
        this.onMediaStreamTrackAddedDelegate = new MulticastDelegate2();
        this.onMediaStreamTrackRemovedDelegate = new MulticastDelegate2();
        this.device = new Device();
        this.consumers = new Map();
        this.videoProducers = new Map();
        this.closed = false;
        this.mediaStreams = [];
        this.onSendDisconnectTimeout = () => __awaiter(this, void 0, void 0, function* () {
            try {
                console.log(`Client ${usersService.getDisplayName(this.peer.webRTC.remoteClientId)} no longer responds`);
                const iceServers = yield fetchWebRTCStunTurnServers(true);
                yield this.recreateSendTransport(iceServers);
            }
            catch (error) {
                console.error("Error onSendDisconnectTimeout", error);
            }
        });
        this.onRecvDisconnectTimeout = () => {
            console.log(`Client ${usersService.getDisplayName(this.peer.webRTC.remoteClientId)} no longer responds`);
            this.recreateRecv();
        };
    }
    join() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.device.load({});
            if (this.closed)
                return;
            const iceServers = yield fetchWebRTCStunTurnServers();
            if (this.closed)
                return;
            yield this.createSendTransport(iceServers);
            if (this.closed)
                return;
            yield this.createRecvTransport(iceServers);
            if (this.closed)
                return;
            this.onReadyChangedDelegate.invoke(this.isReady());
        });
    }
    isReady() {
        var _a, _b, _c, _d;
        if (((_a = this.sendTransport) === null || _a === void 0 ? void 0 : _a.connectionState) === "new" || ((_b = this.sendTransport) === null || _b === void 0 ? void 0 : _b.connectionState) === "connected") {
            if (((_c = this.recvTransport) === null || _c === void 0 ? void 0 : _c.connectionState) === "new" || ((_d = this.recvTransport) === null || _d === void 0 ? void 0 : _d.connectionState) === "connected") {
                return true;
            }
        }
        return false;
    }
    addLocalVideoStream(stream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!stream) {
                console.error("[multiplayer] MediaConnections.addLocalMediaStream: stream is not defined");
                return;
            }
            if (!this.sendTransport) {
                yield this.peer.waitForTransport("send");
            }
            const sendTracks = () => __awaiter(this, void 0, void 0, function* () {
                if (!this.sendTransport) {
                    throw new Error("addLocalVideoStream: sendTransport is not defined");
                }
                for (const track of stream.getVideoTracks()) {
                    if (this.videoProducers.has(track.id)) {
                        console.warn("[multiplayer] MediaConnections.addLocalMediaStream: video producer already exists");
                        continue;
                    }
                    setVideoTrackContentHint(track, "detail");
                    const mediaProducer = yield this.sendTransport.produce(generateUUID(), {
                        stream,
                        track,
                        stopTracks: false,
                        zeroRtpOnPause: false,
                        disableTrackOnPause: false
                    });
                    this.videoProducers.set(track.id, mediaProducer);
                    mediaProducer.onTransportcloseDelegate.add(() => {
                        this.disableVideoTrack(track);
                    });
                    mediaProducer.onTrackendedDelegate.add(() => {
                        this.disableVideoTrack(track);
                    });
                }
            });
            if (this.isReady()) {
                yield sendTracks();
            }
            else {
                yield new Promise((resolve, reject) => {
                    const onReadyChanged = (ready) => {
                        if (!ready)
                            return;
                        this.onReadyChangedDelegate.remove(onReadyChanged);
                        void sendTracks().then(resolve, reject);
                    };
                    this.onReadyChangedDelegate.add(onReadyChanged);
                });
            }
        });
    }
    setLocalAudioStream(stream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (stream.getAudioTracks().length !== 1) {
                throw new Error("setLocalAudioStream: stream must have exactly one audio track");
            }
            if (!this.sendTransport) {
                yield this.peer.waitForTransport("send");
            }
            this.removeLocalAudioStream();
            const sendAudioTrack = () => __awaiter(this, void 0, void 0, function* () {
                if (!this.sendTransport) {
                    throw new Error("setLocalAudioStream: sendTransport is not defined");
                }
                const track = stream.getAudioTracks()[0];
                this.audioProducer = yield this.sendTransport.produce(generateUUID(), {
                    stream,
                    track,
                    stopTracks: false,
                    encodings: this.getEncodingsFromPreferences(),
                    codecOptions: { opusStereo: false, opusDtx: true },
                    codecs: this.getCodecsFromPreferences(),
                    zeroRtpOnPause: false,
                    disableTrackOnPause: false
                });
                this.audioProducer.onTransportcloseDelegate.add(() => {
                    this.audioProducer = undefined;
                });
            });
            if (this.isReady()) {
                sendAudioTrack();
            }
            else {
                this.onReadyChangedDelegate.add((ready) => {
                    if (ready) {
                        sendAudioTrack();
                        this.onReadyChangedDelegate.remove(sendAudioTrack);
                    }
                });
            }
        });
    }
    removeLocalVideoStream(stream) {
        for (const track of stream.getTracks()) {
            this.disableVideoTrack(track);
        }
    }
    removeLocalAudioStream() {
        if (this.audioProducer) {
            this.audioProducer.close();
            this.audioProducer = undefined;
        }
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.closed = true;
            this.clearSendDisconnectTimeout();
            this.clearRecvDisconnectTimeout();
            this.closeSendTransport();
            this.closeRecvTransport();
        });
    }
    setRtpEncodingParametersByKind(kind, params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.sendTransport) {
                yield this.peer.waitForTransport("send");
                if (!this.sendTransport) {
                    throw new Error("setRtpEncodingParametersByKind: sendTransport is not defined");
                }
            }
            this.sendTransport.setRtpEncodingParametersByKind(kind, params);
        });
    }
    updateBandwidthRestriction(bitrate_1, priority_1, networkPriority_1) {
        return __awaiter(this, arguments, void 0, function* (bitrate, priority, // RTCPriorityType,
        networkPriority, // RTCPriorityType,
        kind = undefined) {
            if (!this.sendTransport) {
                yield this.peer.waitForTransport("send");
                if (!this.sendTransport) {
                    throw new Error("updateBandwidthRestriction: sendTransport is not defined");
                }
            }
            this.sendTransport.updateBandwidthRestriction(bitrate, priority, networkPriority, kind);
        });
    }
    disableVideoTrack(track) {
        const producer = this.videoProducers.get(track.id);
        if (!producer)
            return;
        producer.close();
        this.videoProducers.delete(track.id);
    }
    getCodecsFromPreferences() {
        let preferredCodecs;
        const preferredAudioCodec = preferencesService.getPreferredValue("audioCodec");
        if (preferredAudioCodec) {
            try {
                const codecs = getSupportedAudioCodecs();
                const [mimeType, channels, clockRate, sdpFmtpLine] = preferredAudioCodec.split(" ");
                const codecIndex = codecs.findIndex((c) => {
                    if (c.mimeType !== mimeType)
                        return false;
                    if (c.channels && c.channels !== channels)
                        return false;
                    if (c.clockRate && c.clockRate !== clockRate)
                        return false;
                    return !(c.sdpFmtpLine && c.sdpFmtpLine !== sdpFmtpLine);
                });
                if (codecIndex > 0) {
                    const preferredCodec = codecs[codecIndex];
                    codecs.splice(codecIndex, 1);
                    codecs.unshift(preferredCodec);
                    preferredCodecs = codecs;
                }
            }
            catch (error) {
                console.error("[multiplayer]", error);
            }
        }
        return preferredCodecs !== null && preferredCodecs !== void 0 ? preferredCodecs : getSupportedAudioCodecs();
    }
    getEncodingsFromPreferences() {
        const encoding = {};
        if (preferencesService.getPreferredValue("opusDtx", "")) {
            ;
            encoding.dtx = true;
        }
        const maxBitrate = preferencesService.getNumberPreferredValue("audioBandwidth", undefined);
        if (maxBitrate !== undefined) {
            encoding.maxBitrate = maxBitrate * 1000;
        }
        if (preferencesService.getPreferredValue("audioMaxFramerate", "")) {
            ;
            encoding.maxFramerate = preferencesService.getNumberPreferredValue("audioMaxFramerate", undefined);
        }
        if (preferencesService.getPreferredValue("audioPriority", "")) {
            ;
            encoding.priority = preferencesService.getPreferredValue("audioPriority", undefined);
        }
        if (preferencesService.getPreferredValue("audioNetworkPriority", "")) {
            ;
            encoding.networkPriority = preferencesService.getPreferredValue("audioNetworkPriority", undefined);
        }
        if (preferencesService.getBooleanPreferredValue("adaptivePtime", undefined) !== undefined) {
            ;
            encoding.adaptivePtime = preferencesService.getBooleanPreferredValue("adaptivePtime", undefined);
        }
        if (preferencesService.getPreferredValue("opusPtime", "")) {
            ;
            encoding.ptime = preferencesService.getNumberPreferredValue("opusPtime", undefined);
        }
        return [encoding];
    }
    onNewConsumer(
    // TODO: Check if this is still relevant
    producerId, mediaId, kind, sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.recvTransport) {
                yield this.peer.waitForTransport("recv");
                if (!this.recvTransport) {
                    throw new Error("onNewConsumer: recvTransport is not defined");
                }
            }
            const consumer = yield this.recvTransport.consume({
                id: mediaId,
                producerId,
                kind,
                sdp,
                rtpParameters: {
                    codecs: [],
                    mid: mediaId
                }
            });
            this.consumers.set(consumer.id, consumer);
            consumer.onTransportcloseDelegate.add(() => {
                this.consumers.delete(consumer.id);
            });
            consumer.onTrackEndedDelegate.add(() => { });
        });
    }
    handleOffer(sdp, mediaId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.recvTransport) {
                yield this.peer.waitForTransport("recv");
                if (!this.recvTransport) {
                    throw new Error(`handleOffer: recvTransport ist not defined ${this.peer.webRTC.remoteClientId}`);
                }
            }
            yield this.recvTransport.handleOffer(sdp, mediaId);
        });
    }
    handleAnswer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.sendTransport) {
                yield this.peer.waitForTransport("send");
                if (!this.sendTransport) {
                    throw new Error(`handleAnswer: sendTransport ist not defined ${this.peer.webRTC.remoteClientId}`);
                }
            }
            yield this.sendTransport.handleAnswer(sdp);
        });
    }
    handleICE(from, candidate) {
        return __awaiter(this, void 0, void 0, function* () {
            const transport = yield this.getOppositeTransport(from.id);
            yield transport.handleIce(candidate);
        });
    }
    handleNegotiate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closed) {
                return;
            }
            let sendTransport = this.sendTransport;
            if (sendTransport === undefined) {
                yield this.peer.waitForTransport("send");
                if (this.closed) {
                    return;
                }
                sendTransport = this.sendTransport;
                if (sendTransport === undefined || sendTransport.closed) {
                    throw new Error("handleNegotiate: sendTransport is not defined");
                }
            }
            if (sendTransport.closed) {
                return;
            }
            try {
                yield sendTransport.createOffer();
            }
            catch (error) {
                if (isExpectedTransportShutdownError(error)) {
                    return;
                }
                throw error;
            }
        });
    }
    createSendTransport(iceServers) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = "send";
            this.sendTransport = this.device.createSendTransport(this.peer.webRTC.remoteClientId, {
                id,
                iceCandidates: [],
                dtlsParameters: { fingerprints: [] },
                iceServers,
                proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS
            });
            this.peer.registerTransport(id, this.sendTransport);
            this.sendTransport.onConnectDelegate = (dtlsParameters, callback, errback) => {
                dtlsParameters;
                errback;
                callback();
            };
            this.sendTransport.onConnectionstatechangeDelegate.set((connectionState) => {
                this.checkSendIceStatus(connectionState);
                this.onReadyChangedDelegate.invoke(this.isReady());
            });
            this.sendTransport.onProduceDelegate = (kind, rtpParameters) => __awaiter(this, void 0, void 0, function* () {
                try {
                    kind;
                    rtpParameters;
                    return ``;
                }
                catch (error) {
                    throw error;
                }
            });
        });
    }
    checkSendIceStatus(connectionState) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // If the ICE connection state is failed, we force an ICE restart
                if (connectionState === "failed") {
                    loggingService.error("Send: ice connection failed");
                    yield this.restartSendICE();
                }
                else if (connectionState === "disconnected") {
                    loggingService.error("Send: ice connection disconnected");
                    this.setSendDisconnectTimeout();
                }
                else if (connectionState === "connecting") {
                    this.clearSendDisconnectTimeout();
                }
                else if (connectionState === "connected") {
                    this.clearSendDisconnectTimeout();
                }
            }
            catch (error) {
                console.error("[multiplayer] Error checkSendIceStatus", error);
            }
        });
    }
    restartSendICE() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.sendTransport && !this.sendTransport.closed) {
                    yield this.iceRestart(this.sendTransport);
                }
                else {
                    const iceServers = yield fetchWebRTCStunTurnServers(true);
                    yield this.recreateSendTransport(iceServers);
                }
            }
            catch (err) {
                console.error("[multiplayer]", err);
            }
        });
    }
    recreateSendTransport(iceServers) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closed) {
                return;
            }
            console.log("[multiplayer] Recreating send transport ICE", this.peer.webRTC.clientId);
            this.clearSendDisconnectTimeout();
            this.closeSendTransport();
            yield this.createSendTransport(iceServers);
        });
    }
    closeSendTransport() {
        if (this.sendTransport) {
            if (!this.sendTransport.closed) {
                this.sendTransport.close();
            }
            this.sendTransport = undefined;
        }
        if (this.audioProducer) {
            this.audioProducer = undefined;
        }
        this.videoProducers.clear();
    }
    createRecvTransport(iceServers) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = "recv";
            this.recvTransport = this.device.createRecvTransport(this.peer.webRTC.remoteClientId, {
                id,
                iceCandidates: [],
                dtlsParameters: { fingerprints: [] },
                iceServers
            });
            this.peer.registerTransport(id, this.recvTransport);
            this.recvTransport.onNewconsumerDelegate.add((consumer) => {
                this.mediaStreams.push(consumer.stream);
                this.onMediaStreamTrackAddedDelegate.invoke(consumer.stream, consumer.track);
                consumer.onTrackEndedDelegate.add(() => {
                    this.mediaStreams = this.mediaStreams.filter((stream) => stream !== consumer.stream);
                    this.onMediaStreamTrackRemovedDelegate.invoke(consumer.stream, consumer.track);
                });
                consumer.onStreamChangedDelegate.add((oldStream) => {
                    this.mediaStreams = this.mediaStreams.filter((stream) => stream !== consumer.stream);
                    this.onMediaStreamTrackRemovedDelegate.invoke(oldStream, consumer.track);
                    this.mediaStreams.push(consumer.stream);
                    this.onMediaStreamTrackAddedDelegate.invoke(consumer.stream, consumer.track);
                });
            });
            this.recvTransport.onConnectionstatechangeDelegate.set((connectionState) => {
                this.checkRecvIceStatus(connectionState);
                this.onReadyChangedDelegate.invoke(this.isReady());
            });
        });
    }
    closeRecvTransport() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.recvTransport) {
                if (!this.recvTransport.closed) {
                    this.recvTransport.close();
                }
                this.recvTransport = undefined;
            }
            this.consumers.clear();
        });
    }
    checkRecvIceStatus(connectionState) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (connectionState === "failed") {
                    loggingService.error("Recv: ice connection failed");
                    yield this.restartRecvICE();
                }
                else if (connectionState === "connecting") {
                    this.clearRecvDisconnectTimeout();
                }
                else if (connectionState === "disconnected") {
                    this.setRecvDisconnectTimeout();
                }
                else if (connectionState === "connected") {
                    this.clearRecvDisconnectTimeout();
                }
            }
            catch (error) {
                console.error("[multiplayer] Error checkRecvIceStatus", error);
            }
        });
    }
    recreateRecvTransport(iceServers) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.closeRecvTransport();
            yield this.createRecvTransport(iceServers);
        });
    }
    restartRecvICE() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closed) {
                return;
            }
            try {
                if (this.recvTransport && !this.recvTransport.closed) {
                    yield this.iceRestart(this.recvTransport);
                }
                else {
                    const iceServers = yield fetchWebRTCStunTurnServers(true);
                    yield this.recreateRecvTransport(iceServers);
                }
            }
            catch (err) {
                console.error("[multiplayer] Error restartRecvICE", err);
            }
        });
    }
    iceRestart(transport) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closed) {
                return;
            }
            const iceParameters = {
                usernameFragment: "",
                password: ""
            };
            yield transport.restartIce({ iceParameters });
        });
    }
    recreateRecv() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.closed) {
                return;
            }
            this.clearRecvDisconnectTimeout();
            try {
                yield this.closeRecvTransport();
                const iceServers = yield fetchWebRTCStunTurnServers(true);
                yield this.createRecvTransport(iceServers);
            }
            catch (error) {
                console.error("Error recreateRecv", error);
            }
        });
    }
    setSendDisconnectTimeout() {
        if (this.closed) {
            return;
        }
        if (this.sendDisconnectedTimeout !== undefined) {
            return;
        }
        this.sendDisconnectedTimeout = window.setTimeout(this.onSendDisconnectTimeout, DISCONNECT_TIMEOUT);
    }
    clearSendDisconnectTimeout() {
        if (this.sendDisconnectedTimeout === undefined) {
            return;
        }
        clearTimeout(this.sendDisconnectedTimeout);
        this.sendDisconnectedTimeout = undefined;
    }
    setRecvDisconnectTimeout() {
        if (this.closed) {
            return;
        }
        if (this.recvDisconnectedTimeout !== undefined) {
            return;
        }
        this.recvDisconnectedTimeout = window.setTimeout(this.onRecvDisconnectTimeout, DISCONNECT_TIMEOUT);
    }
    clearRecvDisconnectTimeout() {
        if (this.recvDisconnectedTimeout === undefined) {
            return;
        }
        clearTimeout(this.recvDisconnectedTimeout);
        this.recvDisconnectedTimeout = undefined;
    }
    getOppositeTransport(fromId) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (fromId) {
                case "send":
                    if (!this.recvTransport) {
                        return yield this.peer.waitForTransport("recv");
                    }
                    return this.recvTransport;
                case "recv":
                    if (!this.sendTransport) {
                        return yield this.peer.waitForTransport("send");
                    }
                    return this.sendTransport;
            }
            throw new TypeError(`Can't find opposite transport for ${fromId}`);
        });
    }
}
const isExpectedTransportShutdownError = (error) => {
    if (!(error instanceof Error))
        return false;
    const message = error.message.toLowerCase();
    return ((message.includes("awaitqueue") && message.includes("closed")) ||
        (message.includes("transport") && message.includes("closed")) ||
        message.includes("invalidstateerror") ||
        message.includes("peer can't answer"));
};
